import { Component, DestroyRef, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormControl, ReactiveFormsModule, ValidatorFn } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { InputNumber } from 'primeng/inputnumber';
import { ButtonModule } from 'primeng/button';
import { Toast } from 'primeng/toast';
import { Tooltip } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { Subject, filter } from 'rxjs';
import { FilingFormStateService } from '../../../services/mbrs/filing-form-state.service';
import { CrossReferenceService } from '../../../services/mbrs/cross-reference.service';
import { CalculationService } from '../../../services/mbrs/calculation.service';
import { CrossTabLayoutChangeNotifierService } from '../../../services/mbrs/cross-tab-layout-change-notifier.service';
import { NegativeHighlightDirective } from '../../../directives/negative-highlight.directive';
import { SchemaNumericDirective } from '../../../directives/schema-numeric.directive';
import { NumericFieldPolicy, resolveNumericPolicy } from '../../../utils/numeric-policy.util';
import { Router } from '@angular/router';
import { ReloadPromptComponent } from '../../common/reload-prompt/reload-prompt.component';
import { ReloadBannerComponent } from '../../common/reload-banner/reload-banner.component';
import { stripAllValidatorsRecursively } from '../../../utils/form-validation.util';
import {
  FORM_CALCULATION_DEBOUNCE_MS,
} from '../../../utils/debounce-times.util';
import { buildFormEditPipeline } from '../../../utils/form-edit-pipeline.util';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import { ExtractedSumWarningService, EXTRACTED_SUM_MARKER } from '../../../services/mbrs/extracted-sum-warning.service';
import { SummableControl } from '../../../services/mbrs/extracted-sum-warning';
import { ExtractedSumBaselineDebugService } from '../../../services/common/extracted-sum-baseline-debug.service';
import {
  TOAST_DURATION_DEFAULT_MS,
  TOAST_DURATION_LONG_MS,
  TOAST_DURATION_SHORT_MS,
} from '../../../utils/toast-durations.util';
import { PlaceholderPipe } from '../../../utils/pipes/placeholder.pipe';
import { isControlMandatory } from '../../../utils/required-field.util';
import { RequiredMarkerComponent } from '../../common/required-marker/required-marker.component';

import { SheetLoaderComponent } from '../../common/sheet-loader/sheet-loader.component';
import { FieldLinkTargetDirective } from '../../../directives/field-link-target.directive';
import { computedControlPredicate, revealValidationErrors } from '../../../utils/validation-reveal.util';
import { adoptIntoAutoSaveSheet } from '../../../utils/adopt-remote-sheet-state.util';
import {
  LoadTrace,
  NO_OP_LOAD_TRACE,
  SheetLoadTraceService,
} from '../../../services/mbrs/sheet-load-trace.service';
import { SHEET_LOAD_STEPS } from '../../../utils/sheet-load-trace.util';
import {
  classifySheetInitError,
  fetchSheetSchema,
} from '../../../utils/schema-asset.util';
interface FieldControl {
  qname: string;
  label: string;
  type: string;
  ui: {
    control: string;
    currency?: string;
    placeholder?: string;
    dualColumn?: boolean;
    calculated?: boolean;
    level?: number;
    note?: string;
    style?: string;
  };
}

interface FormSchema {
  role: string;
  sheetId: string;
  label: string;
  description: string;
  controls: FieldControl[];
}

@Component({
  selector: 'app-role15-320100-analysis-of-profit-or-loss-by-nature',
  standalone: true,
  imports: [
    SheetLoaderComponent,
    RequiredMarkerComponent,
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    InputNumber,
    ButtonModule,
    Toast,
    Tooltip,
    NegativeHighlightDirective,

    SchemaNumericDirective,
    ReloadPromptComponent,
    ReloadBannerComponent,
    PlaceholderPipe,
    FieldLinkTargetDirective
  ],
  providers: [MessageService],
  templateUrl: './role15-320100-analysis-of-profit-or-loss-by-nature.component.html',
  styleUrls: ['./role15-320100-analysis-of-profit-or-loss-by-nature.component.css']
})
export class Role15320100AnalysisOfProfitOrLossByNatureComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private messageService = inject(MessageService);
  private loadTrace = inject(SheetLoadTraceService);
  /**
   * This mount's load trace. Opened as the first act of `ngOnInit` and closed
   * either by the terminal step mark or by the init catch, so a failure reports
   * which of the seven load steps this sheet actually reached.
   *
   * @see src/app/utils/sheet-load-trace.util.ts
   */
  private loadTraceHandle: LoadTrace = NO_OP_LOAD_TRACE;
  private stateService = inject(FilingFormStateService);
  private crossRefService = inject(CrossReferenceService);
  private calculationService = inject(CalculationService);
  private layoutChangeNotifier = inject(CrossTabLayoutChangeNotifierService);
  private destroyRef = inject(DestroyRef);
  private destroy$ = new Subject<void>();
  private extractedSumWarning = inject(ExtractedSumWarningService);
  private baselineDebug = inject(ExtractedSumBaselineDebugService);
  private validationErrors = new Map<string, string[]>();
  private readonly sheetKey = SHEET_KEYS.analysisOfProfitOrLossByNature;

  analysisForm!: FormGroup;
  schema: FormSchema | null = null;
  /**
   * Card header carrying the filing's submission type, e.g.
   * "Analysis of Profit or Loss (By Nature) (FS-MPERS)".
   *
   * Two rules, both of which this sheet used to break:
   *  - the name comes from the canonical `SHEET_DISPLAY_NAMES` map (via
   *    `getSheetTitleWithSubmissionType`), never a literal repeated here;
   *  - it is a LIVE getter, never an `ngOnInit` snapshot — see the cold-mount
   *    rule on `FilingFormStateService.getFormTitleWithSubmissionType()`.
   */
  get formTitle(): string {
    return this.stateService.getSheetTitleWithSubmissionType(
      SHEET_KEYS.analysisOfProfitOrLossByNature
    );
  }
  isLoading = true;
  isConsolidated = false;
  hasPreviousYear = false;
  // Init-time snapshot of the schema-variant decision input. The form is built
  // against either role-320100-mfrs or role-320100-mpers; if isMFRS() flips
  // upstream the in-memory FormGroup is wired to the wrong taxonomy variant
  // and must be rebuilt via reload.
  private initialIsMFRS = false;

  showReloadPrompt = signal<boolean>(false);
  reloadPromptDismissed = signal<boolean>(false);

  /**
   * Get currency symbol with unit from Role-1
   * e.g., "RM'000", "000", "€'000"
   */
  get currencyUnit(): string {
    const symbol = this.crossRefService.getCurrencySymbol();
    return `${symbol}'000`;
  }

  /**
   * Get period header for current year
   * e.g., "For the year ended 31 December 2024"
   */
  get periodHeaderCurrent(): string {
    return this.crossRefService.getPeriodHeader('current');
  }

  /**
   * Get period header for previous year
   * e.g., "For the year ended 31 December 2023"
   */
  get periodHeaderPrevious(): string {
    return this.crossRefService.getPeriodHeader('previous');
  }

  ngOnInit(): void {
    // Open this mount's load trace before anything else, so a failure
    // anywhere below reports which load steps were reached.
    this.loadTraceHandle = this.loadTrace.beginSheetLoad(
      '15-320100',
      this.stateService.getConversionId(),
      this.destroyRef,
    );
    this.baselineDebug.bindGlobalShortcut(this.destroyRef);
    this.checkConsolidatedStatus();
    this.loadSchema();
    this.setupLayoutReactivity();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Check consolidation status and comparative information from Role-2
   */
  private checkConsolidatedStatus(): void {
    const scopeOfFilingData = this.stateService.getSheetData(SHEET_KEYS.scopeOfFiling);
    this.isConsolidated = scopeOfFilingData?.['ssmt:DisclosureOfWhetherFinancialStatementsAreConsolidated'] === 'Yes';

    const comparativeInfo = scopeOfFilingData?.['ssmt:DisclosureOfComparativeInformation'];
    this.hasPreviousYear =
      comparativeInfo === 'Comparative information presented' ||
      comparativeInfo === 'Restated comparative information';

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('Consolidated status:', this.isConsolidated);
    // console.log('Has previous year:', this.hasPreviousYear);
  }

  // Why: pure predicate, no mutation of isConsolidated / hasPreviousYear /
  // initialIsMFRS. This component has a dual structural gate: column count
  // (consolidated x previous-year) AND schema variant (MFRS vs MPERS). Both
  // are decided at init and bake into the in-memory FormGroup, so a change
  // to either invalidates the layout. Compare-only, return boolean, let the
  // notifier surface the prompt.
  private detectLayoutFlagsChanged(): boolean {
    const scopeOfFilingData = this.stateService.getSheetData(SHEET_KEYS.scopeOfFiling);
    const freshConsolidated =
      scopeOfFilingData?.['ssmt:DisclosureOfWhetherFinancialStatementsAreConsolidated'] === 'Yes';
    const comparativeInfo = scopeOfFilingData?.['ssmt:DisclosureOfComparativeInformation'];
    const freshPreviousYear =
      comparativeInfo === 'Comparative information presented' ||
      comparativeInfo === 'Restated comparative information';
    const freshIsMFRS = this.crossRefService.isMFRS();

    return (
      freshConsolidated !== this.isConsolidated ||
      freshPreviousYear !== this.hasPreviousYear ||
      freshIsMFRS !== this.initialIsMFRS
    );
  }

  private router = inject(Router);

  private setupLayoutReactivity(): void {
    this.layoutChangeNotifier
      .watchSubmissionCommitted({
        sheetKey: this.sheetKey,
        slicesOfInterest: [SHEET_KEYS.filingPrerequisites, SHEET_KEYS.filingInfo, SHEET_KEYS.scopeOfFiling],
        detectChanged: () => this.detectLayoutFlagsChanged(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.reloadPromptDismissed()) {
          this.showReloadPrompt.set(true);
        }
      });
  }

  /**
   * Cross-tab live adoption — when a sibling tab edits THIS sheet's own
   * analysisOfProfitOrLossByNature slice, `applyRemoteChange` rehydrates
   * the shared state and publishes `changedSheets` on
   * `remoteUpdateApplied$`. `adoptRemoteState()` below repaints this
   * sheet's form from that state (silently, no emitted valueChanges).
   */
  private setupCrossTabLiveAdoption(): void {
    this.stateService.remoteUpdateApplied$
      .pipe(
        filter(({ streamKey }) => streamKey === 'modifiedState'),
        filter(({ changedSheets }) => changedSheets.includes(SHEET_KEYS.analysisOfProfitOrLossByNature)),
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.analysisOfProfitOrLossByNature)),
        filter(() => !!this.schema && !!this.analysisForm),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.adoptRemoteState();
      });
  }

  /**
   * Cross-tab adoption: a sibling tab edited this sheet, `applyRemoteChange` has
   * already rehydrated the shared state, and this repaints the form from it.
   *
   * MUST NOT write back to state — see the invariant on `adoptIntoAutoSaveSheet`.
   * The validation tail below runs for DISPLAY only; the sibling already
   * persisted its own results.
   *
   * Developer Note (TODO): silent adoption is the P.O. decision of 2026-08-17 and
   * is revisitable. If feedback is later wanted, the two options considered were a
   * brief per-row highlight (preferred — it answers "why did that number change?"
   * at the spot it changed) and a per-adoption toast (cheaper, but says nothing
   * about WHAT changed and stacks on repeated sibling edits).
   */
  private adoptRemoteState(): void {
    adoptIntoAutoSaveSheet(
      this.analysisForm,
      this.stateService.getSheetData(SHEET_KEYS.analysisOfProfitOrLossByNature)
    );
    this.calculateTotals();
  }

  onReload(): void {
    window.location.reload();
  }

  onDismiss(): void {
    this.showReloadPrompt.set(false);
    this.reloadPromptDismissed.set(true);
    this.disableAllValidations();
  }

  onReturnToFiles(): void {
    this.showReloadPrompt.set(false);
    this.router.navigate(['/mbrs/xbrl-files']);
  }

  // Why: one-way door. Once the user dismisses the reload prompt the
  // in-memory form schema is known-stale vs. upstream scope; persisting
  // any further validation result would be misleading. We strip every
  // validator from every control (and the root FormGroup) so no spurious
  // errors accumulate before the user reloads. There is deliberately no
  // re-enable path — a fresh ngOnInit (via reload) is the only recovery.
  private disableAllValidations(): void {
    stripAllValidatorsRecursively(this.analysisForm);
  }

  /**
   * Load form schema from JSON
   * Dynamically loads MFRS or MPERS schema based on accounting standard from Role-0
   */
  private async loadSchema(): Promise<void> {
    this.isLoading = true;

    // Wait for retrieve-mbrs-data hydration so the accounting standard is
    // populated before schema selection — a mid-hydration refresh mount would
    // otherwise fall back to the MFRS default and MPERS values would never bind
    // (blank form).
    await this.stateService.waitForMbrsHydration('role15');
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.hydrationSettled);

    // Determine which schema to load based on accounting standard
    const isMFRS = this.crossRefService.isMFRS();
    this.initialIsMFRS = isMFRS;
    const schemaFile = isMFRS ? 'role-320100-mfrs.form.json' : 'role-320100-mpers.form.json';

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log(`Loading schema: ${schemaFile} (MFRS: ${isMFRS})`);

    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaSelected);
    fetchSheetSchema<FormSchema>(
        this.loadTraceHandle,
        `assets/forms/${schemaFile}`,
      )
      .then((data: FormSchema) => {
        this.schema = data;
        this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaParsed);

        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log('Schema loaded:', this.schema);

        this.buildForm();
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.formBuilt);
        this.setupEditPipeline();

        // Reveal the sheet's already-computed errors now that the form exists
        // and has been populated. Deterministic replacement for the filing-form
        // container's setTimeout-driven markAllAsTouched, which silently no-ops
        // when a sheet's async init has not yet assigned its FormGroup — see
        // revealValidationErrors' docblock. Touch-only: cannot move the verdict.
        revealValidationErrors(this.analysisForm, {
          isComputed: computedControlPredicate(this.schema?.controls),
        });
        this.loadSavedState();
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
        this.setupCrossTabLiveAdoption();
        this.isLoading = false;
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);

        // Snapshot baseline after all debounced valueChanges settle
        setTimeout(() => {
          this.stateService.markSheetInitializedWithStatus(SHEET_KEYS.analysisOfProfitOrLossByNature, false);
        }, 600);
      })
      .catch(error => {
        console.error('Error loading schema:', error);
        // Classified rather than reported as a bare schema failure: this one
        // `.catch` also covers the cross-sheet preload, buildForm, the edit
        // pipeline and loadSavedState, so "Failed to load form schema" named
        // the wrong step for most of what can land here.
        this.loadTraceHandle.fail(classifySheetInitError(error), error);
        this.isLoading = false;
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
      });
  }

  /**
   * Build reactive form from schema
   */
  private buildForm(): void {
    if (!this.schema) return;

    const formControls: any = {};

    this.schema.controls.forEach(control => {
      // Skip section headers
      if (control.type === 'SectionHeaderType') return;

      const validators: ValidatorFn[] = [];

      if (control.ui.dualColumn) {
        // Use null for non-calculated fields, 0 for calculated/total fields
        // This ensures unmapped API data fields remain as NULL (not converted to 0)
        const defaultValue = control.ui.calculated ? 0 : null;

        // Scenario 1: Consolidated + Previous Year = 4 columns
        if (this.isConsolidated && this.hasPreviousYear) {
          formControls[control.qname + 'GroupCurrent'] = new FormControl(defaultValue, validators);
          formControls[control.qname + 'GroupPrevious'] = new FormControl(defaultValue, validators);
          formControls[control.qname + 'CompanyCurrent'] = new FormControl(defaultValue, validators);
          formControls[control.qname + 'CompanyPrevious'] = new FormControl(defaultValue, validators);
        }
        // Scenario 2: Consolidated only = 2 columns (Group, Company)
        else if (this.isConsolidated && !this.hasPreviousYear) {
          formControls[control.qname + 'GroupCurrent'] = new FormControl(defaultValue, validators);
          formControls[control.qname + 'CompanyCurrent'] = new FormControl(defaultValue, validators);
        }
        // Scenario 3: Previous Year only = 2 columns (Current, Previous)
        else if (!this.isConsolidated && this.hasPreviousYear) {
          formControls[control.qname + 'Current'] = new FormControl(defaultValue, validators);
          formControls[control.qname + 'Previous'] = new FormControl(defaultValue, validators);
        }
        // Scenario 4: No consolidation, no previous year = 1 column (Current)
        else {
          formControls[control.qname + 'Current'] = new FormControl(defaultValue, validators);
        }
      }
    });

    this.analysisForm = this.fb.group(formControls);

    // Disable calculated fields to prevent manual editing
    this.disableCalculatedFields();
  }

  /**
   * Disable calculated fields programmatically
   * Called after form creation to prevent "changed after checked" warnings
   */
  private disableCalculatedFields(): void {
    if (!this.schema) return;

    this.schema.controls.forEach(control => {
      if (this.isCalculatedField(control)) {
        // Disable all column variants for this calculated field
        if (this.isConsolidated && this.hasPreviousYear) {
          this.analysisForm.get(control.qname + 'GroupCurrent')?.disable({ emitEvent: false });
          this.analysisForm.get(control.qname + 'GroupPrevious')?.disable({ emitEvent: false });
          this.analysisForm.get(control.qname + 'CompanyCurrent')?.disable({ emitEvent: false });
          this.analysisForm.get(control.qname + 'CompanyPrevious')?.disable({ emitEvent: false });
        } else if (this.isConsolidated && !this.hasPreviousYear) {
          this.analysisForm.get(control.qname + 'GroupCurrent')?.disable({ emitEvent: false });
          this.analysisForm.get(control.qname + 'CompanyCurrent')?.disable({ emitEvent: false });
        } else if (!this.isConsolidated && this.hasPreviousYear) {
          this.analysisForm.get(control.qname + 'Current')?.disable({ emitEvent: false });
          this.analysisForm.get(control.qname + 'Previous')?.disable({ emitEvent: false });
        } else {
          this.analysisForm.get(control.qname + 'Current')?.disable({ emitEvent: false });
        }
      }
    });
  }

  /**
   * Check if control is a section header
   */
  isSectionHeader(control: FieldControl): boolean {
    return control.type === 'SectionHeaderType';
  }

  /**
   * Get header CSS class based on level
   */
  getHeaderClass(level?: number): string {
    if (level === 1) return 'section-header-major';
    if (level === 2) return 'section-header-minor';
    return 'section-header-minor';
  }

  /**
   * Check if field is calculated
   */
  isCalculatedField(control: FieldControl): boolean {
    return control.ui.calculated === true;
  }

  /**
   * Get row CSS class
   */
  getRowClass(control: FieldControl): string {
    if (control.ui.style === 'grand-total-row') return 'grand-total-row';
    if (control.ui.style === 'total-row') return 'total-row';
    if (control.ui.calculated) return 'calculated-row';
    return '';
  }

  /**
   * Row-type guard: a monetary, dual-column line item (the rows that own an
   * Error Description cell). Used by both the template and getRowErrorDescription.
   */
  isMonetaryItem(control: FieldControl): boolean {
    return control.ui.control === 'number' && control.ui.dualColumn === true;
  }

  /**
   * Resolve the visible columns for the current layout using the SAME inline
   * branch ladder calculateTotals() uses to build suffixes (no resolver exists
   * for this sheet). `key` is the control-name suffix; `label` is the Error
   * Description column heading.
   */
  private visibleSuffixColumns(): { key: string; label: string }[] {
    if (this.isConsolidated && this.hasPreviousYear) {
      return [
        { key: 'GroupCurrent', label: 'Group Current' },
        { key: 'GroupPrevious', label: 'Group Previous' },
        { key: 'CompanyCurrent', label: 'Company Current' },
        { key: 'CompanyPrevious', label: 'Company Previous' },
      ];
    } else if (this.isConsolidated && !this.hasPreviousYear) {
      return [
        { key: 'GroupCurrent', label: 'Group Current' },
        { key: 'CompanyCurrent', label: 'Company Current' },
      ];
    } else if (!this.isConsolidated && this.hasPreviousYear) {
      return [
        { key: 'Current', label: 'Current' },
        { key: 'Previous', label: 'Previous' },
      ];
    } else {
      return [{ key: 'Current', label: 'Current' }];
    }
  }

  // Developer Note (TODO): extracted-sum warning + Error Description column added, but
  // DORMANT + UNVERIFIED end-to-end — role15-320100 (Analysis of P&L by Nature) is NOT a
  // currently-supported/active sheet AND its N8N snapshot is not seeded by
  // filing-form-container.populateSnapshotFromMbrsData (BE work pending). Re-test when the
  // sheet is enabled.
  // (Mapping Status badge column intentionally NOT added — Error Description only.)
  private validateAgainstApiData(): void {
    if (!this.schema) return;
    const warnings = this.extractedSumWarning.computeMismatchWarnings({
      sheetKey: SHEET_KEYS.analysisOfProfitOrLossByNature,
      controls: this.schema.controls,
      columns: this.visibleSuffixColumns(),
      getControlName: (qname, key) => qname + key,
      getCurrentValue: (name) => this.analysisForm.get(name)?.value,
    });
    this.extractedSumWarning.mergeIntoValidationErrors(this.validationErrors, this.schema.controls, warnings);
  }

  /**
   * Render the Error Description cell for a monetary row. role15 only produces
   * suggested-change warnings today; the red-error vs suggested-change split is
   * kept identical to role8/role10/role11 for forward-compat (when real errors land here).
   */
  getRowErrorDescription(control: FieldControl): string {
    if (!this.isMonetaryItem(control)) return '';
    const all = this.validationErrors.get(control.qname) || [];
    const suggestedChanges = all.filter((message) => message.includes(EXTRACTED_SUM_MARKER));
    const errors = all.filter((message) => !message.includes(EXTRACTED_SUM_MARKER));
    let result = '';
    if (errors.length > 0) {
      result += '<span class="error-text">ERROR</span><br>';
      result += '<span class="error-text">' + errors.map((errorText, errorIndex) => `${errorIndex + 1}. ${errorText}`).join('<br><br>') + '</span>';
    }
    if (suggestedChanges.length > 0) {
      if (result) result += '<br><br>';
      result += '<span class="error-text">*</span><span class="suggested-change-text"> SUGGESTED CHANGE</span><br>';
      result += '<span class="suggested-change-text">' + suggestedChanges.map((changeText, changeIndex) => `${changeIndex + 1}. ${changeText}`).join('<br><br>') + '</span>';
    }

    // Ctrl+Alt+B baseline debug overlay (non-production). Appended to whatever
    // real validation text this row already has (often none for a clean total).
    // baselineDebugForControl returns '' unless this row is a qualifying total.
    // Reuses visibleSuffixColumns() + concat keys to mirror validateAgainstApiData's
    // computeMismatchWarnings call exactly.
    if (this.baselineDebug.enabled()) {
      const baselineInfo = this.extractedSumWarning.baselineDebugForControl(
        SHEET_KEYS.analysisOfProfitOrLossByNature,
        control as unknown as SummableControl,
        this.visibleSuffixColumns(),
        (qname, key) => qname + key
      );
      if (baselineInfo) {
        result = result ? `${result}${baselineInfo}` : baselineInfo;
      }
    }

    return result;
  }

  /**
   * Calculate all totals and derived fields for Analysis by Nature
   */
  private calculateTotals(): void {
    if (!this.schema) return;

    // Determine which column suffixes to use
    const suffixes: string[] = [];
    if (this.isConsolidated && this.hasPreviousYear) {
      suffixes.push('GroupCurrent', 'GroupPrevious', 'CompanyCurrent', 'CompanyPrevious');
    } else if (this.isConsolidated && !this.hasPreviousYear) {
      suffixes.push('GroupCurrent', 'CompanyCurrent');
    } else if (!this.isConsolidated && this.hasPreviousYear) {
      suffixes.push('Current', 'Previous');
    } else {
      suffixes.push('Current');
    }

    const calculatedTotals = this.calculationService.calculateTotals_320100(
      this.analysisForm.getRawValue(),
      suffixes
    );

    Object.keys(calculatedTotals).forEach((controlName) => {
      this.analysisForm.get(controlName)?.setValue(calculatedTotals[controlName], { emitEvent: false });
    });

    this.stateService.registerCalculatedKeys(
      SHEET_KEYS.analysisOfProfitOrLossByNature,
      Object.keys(calculatedTotals)
    );
  }


  /**
   * Load saved state from service
   */
  private loadSavedState(): void {
    this.stateService.markSheetInitializing(SHEET_KEYS.analysisOfProfitOrLossByNature);
    const savedData = this.stateService.getSheetData(SHEET_KEYS.analysisOfProfitOrLossByNature);
    if (savedData && Object.keys(savedData).length > 0) {
      this.analysisForm.patchValue(savedData, { emitEvent: false });

      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('Loaded saved state:', savedData);

      // Recalculate totals after loading
      setTimeout(() => {
        this.calculateTotals();
        this.validateAgainstApiData();
      }, 100);
    } else {
      // Report sheet-level status even when no saved data
      setTimeout(() => {
      }, 100);
    }
  }

  /**
   * Wire the single debounced edit pipeline: calc → persist → validate.
   * No guard — the old calc sub had none.
   */
  private setupEditPipeline(): void {
    buildFormEditPipeline({
      source$: this.analysisForm.valueChanges,
      debounceMs: FORM_CALCULATION_DEBOUNCE_MS,
      destroyRef: this.destroyRef,
      calculate: () => this.calculateTotals(),
      persist: () => this.stateService.updateSheetData(
        SHEET_KEYS.analysisOfProfitOrLossByNature,
        this.analysisForm.value
      ),
      validate: () => this.validateAgainstApiData(),
    });
  }

  /**
   * Auto-fill form with demo data
   */
  autoFillDemo(): void {
    if (!this.analysisForm) return;

    const demoData: any = {};

    // Helper function to set values for all column scenarios
    const setDemoValues = (
      qname: string,
      groupCurrentVal: number,
      groupPreviousVal: number,
      companyCurrentVal: number,
      companyPreviousVal: number
    ) => {
      if (this.isConsolidated && this.hasPreviousYear) {
        demoData[qname + 'GroupCurrent'] = groupCurrentVal;
        demoData[qname + 'GroupPrevious'] = groupPreviousVal;
        demoData[qname + 'CompanyCurrent'] = companyCurrentVal;
        demoData[qname + 'CompanyPrevious'] = companyPreviousVal;
      } else if (this.isConsolidated && !this.hasPreviousYear) {
        demoData[qname + 'GroupCurrent'] = groupCurrentVal;
        demoData[qname + 'CompanyCurrent'] = companyCurrentVal;
      } else if (!this.isConsolidated && this.hasPreviousYear) {
        demoData[qname + 'Current'] = companyCurrentVal;
        demoData[qname + 'Previous'] = companyPreviousVal;
      } else {
        demoData[qname + 'Current'] = companyCurrentVal;
      }
    };

    // REVENUE - Sale of Goods
    setDemoValues('ifrs-full:RevenueFromSaleOfTelecommunicationEquipment', 12000000, 11000000, 11500000, 10500000);
    setDemoValues('ssmt-mfrs:RevenueFromSaleOfProperty', 8000000, 7500000, 7800000, 7200000);
    setDemoValues('ssmt-mfrs:RevenueFromSaleOfOtherGoods', 5000000, 4500000, 4700000, 4300000);

    // REVENUE - Rendering of Services
    setDemoValues('ifrs-full:RevenueFromRenderingOfTelecommunicationServices', 6000000, 5500000, 5800000, 5300000);
    setDemoValues('ifrs-full:RevenueFromRenderingOfInformationTechnologyServices', 4000000, 3800000, 3900000, 3700000);

    // REVENUE - Interest Income
    setDemoValues('ifrs-full:InterestIncomeOnLoansAndReceivables', 300000, 280000, 290000, 270000);
    setDemoValues('ifrs-full:InterestIncomeOnOtherFinancialAssets', 150000, 140000, 145000, 135000);

    // REVENUE - Other
    setDemoValues('ifrs-full:RevenueFromDividends', 200000, 180000, 190000, 170000);
    setDemoValues('ifrs-full:RentalIncome', 350000, 320000, 340000, 310000);

    // OTHER INCOME
    setDemoValues('ssmt-mfrs:BadDebtsRecovered', 50000, 40000, 48000, 38000);
    setDemoValues('ifrs-full:GainsOnDisposalsOfPropertyPlantAndEquipment', 120000, 100000, 115000, 95000);
    setDemoValues('ssmt-mfrs:RealisedGainOnForeignExchange', 80000, 70000, 77000, 67000);
    setDemoValues('ssmt-mfrs:UnrealisedGainOnForeignExchange', 40000, 35000, 38000, 33000);

    // EMPLOYEE BENEFITS EXPENSE
    setDemoValues('ifrs-full:WagesAndSalaries', 8000000, 7200000, 7800000, 7000000);
    setDemoValues('ssmt-mfrs:BonusExpenses', 1200000, 1100000, 1150000, 1050000);
    setDemoValues('ifrs-full:PostemploymentBenefitExpenseDefinedContributionPlans', 600000, 550000, 580000, 530000);
    setDemoValues('ifrs-full:SocialSecurityContributions', 400000, 370000, 390000, 360000);

    // OTHER EXPENSES - Depreciation & Amortisation
    setDemoValues('ifrs-full:DepreciationPropertyPlantAndEquipment', 3500000, 3200000, 3400000, 3100000);
    setDemoValues('ifrs-full:AmortisationExpense', 500000, 450000, 480000, 430000);

    // OTHER EXPENSES - Auditor's Remuneration
    setDemoValues('ifrs-full:AuditorsRemunerationForAuditServices', 80000, 75000, 78000, 73000);
    setDemoValues('ifrs-full:AuditorsRemunerationForOtherServices', 20000, 18000, 19000, 17000);

    // OTHER EXPENSES - Director's Remuneration
    setDemoValues('ssmt-mfrs:WagesAndSalariesToDirectors', 500000, 480000, 490000, 470000);
    setDemoValues('ssmt-mfrs:DirectorsFees', 150000, 140000, 145000, 135000);
    setDemoValues('ssmt-mfrs:BonusToDirectors', 100000, 90000, 95000, 85000);

    // OTHER EXPENSES - Other
    setDemoValues('ifrs-full:RentalExpense', 400000, 380000, 390000, 370000);
    setDemoValues('ssmt-mfrs:ResearchAndDevelopmentExpensesNature', 800000, 750000, 780000, 730000);
    setDemoValues('ssmt-mfrs:OtherMiscellaneousExpenses', 1200000, 1100000, 1150000, 1050000);

    // FINANCE INCOME
    setDemoValues('ssmt-mfrs:InterestIncomeDueFromRelatedParties', 100000, 90000, 95000, 85000);
    setDemoValues('ifrs-full:OtherFinanceIncome', 50000, 45000, 48000, 43000);

    this.analysisForm.patchValue(demoData);

    // Trigger calculations
    setTimeout(() => this.calculateTotals(), 100);

    this.messageService.add({
      severity: 'success',
      summary: 'Demo Data Loaded',
      detail: this.isConsolidated && this.hasPreviousYear
        ? 'Form filled with Group/Company and Current/Previous Year demo data'
        : this.isConsolidated
        ? 'Form filled with Group and Company demo data (Current Year only)'
        : this.hasPreviousYear
        ? 'Form filled with Current and Previous Year demo data'
        : 'Form filled with Current Year demo data',
      life: TOAST_DURATION_DEFAULT_MS
    });
  }

  /**
   * Clear form
   */
  clearForm(): void {
    this.analysisForm.reset();
    this.stateService.clearSheetData(SHEET_KEYS.analysisOfProfitOrLossByNature);

    this.messageService.add({
      severity: 'info',
      summary: 'Form Cleared',
      detail: 'All fields have been reset',
      life: TOAST_DURATION_SHORT_MS
    });
  }

  /**
   * Submit form
   */
  submitForm(): void {
    if (this.reloadPromptDismissed()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Reload required',
        detail: 'Please refresh the page to obtain the latest version of this sheet.',
        life: TOAST_DURATION_LONG_MS
      });
      return;
    }
    if (this.analysisForm.valid) {
      const formData = this.analysisForm.value;
      
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('Analysis of Profit or Loss by Nature submitted:', formData);

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Analysis of profit or loss by nature data saved successfully',
        life: TOAST_DURATION_DEFAULT_MS
      });
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please check all required fields',
        life: TOAST_DURATION_DEFAULT_MS
      });
    }
  }
  /**
   * Does this field's label get a `*`?
   *
   * Delegates to the shared `isControlMandatory` so this sheet answers the
   * question the same way as every other.
   *
   * **Wired even though this sheet declares ZERO mandatory controls today.**
   * That is deliberate, and it is the lesson from roles 9, 13 and 15: they
   * were skipped during the 2026-08-11 marker rollout for having no mandatory
   * fields, and when the PO's enumerated totals list flagged their calculated
   * totals hours later the flags landed with nowhere to render — the `*`
   * silently did not appear until smoke testing caught it. A sheet that
   * renders schema-driven labels gets the marker regardless of its current
   * mandatory count, so the next list cannot reopen that gap. The PO's enumerated totals are recorded per field in the sheet's spec under `docs/specifications/sheets/`.
   *
   * `crossSheetValue` is supplied so a cross-sheet `requiredIf` resolves.
   * Same-sheet `selfData` is passed only by sheets keyed by bare qname; the
   * per-cell sheets (`qname[Dimension][Period]`) deliberately omit it, since a
   * same-sheet lookup would silently miss.
   */
  isFieldRequired(control: any): boolean {
    return isControlMandatory(control, {
      crossSheetValue: (sheet, field) =>
        (this.stateService.getSheetData(sheet as any) as any)?.[field],
    });
  }


  /**
   * Memoised per-control numeric policy — this sheet's single declaration
   * point for decimals, grouping and the integer-digit cap, all resolved from
   * the control's own schema record rather than hand-written per binding.
   *
   * Memoised because templates call this once per binding inside an `*ngFor`;
   * an unmemoised `resolveNumericPolicy` would re-resolve on every
   * change-detection cycle. Keyed by `qname`, unique per control in a sheet.
   *
   * `[min]`/`[max]` are deliberately NOT bound from here — `SchemaNumericDirective`
   * assigns them onto the PrimeNG instance directly. See that directive's class
   * docblock for why a template binding coerces an unconstrained `undefined`
   * to `NaN` and silently blocks the minus key on signed monetary fields.
   */
  private policyCache = new Map<string, NumericFieldPolicy>();

  numericPolicy(control: FieldControl): NumericFieldPolicy {
    const cached = this.policyCache.get(control.qname);
    if (cached)
      return cached;
    const resolved = resolveNumericPolicy(control);
    this.policyCache.set(control.qname, resolved);
    return resolved;
  }

}
